/**
 * Unix Timestamp Tool
 * 
 * Converts between Unix timestamps and human-readable dates.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const UnixTimestampTool: ITransformerTool = {
  id: 'unix-timestamp',
  name: 'Unix Timestamp',
  description: 'Convert between Unix timestamps and dates',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['unix', 'timestamp', 'epoch', 'date', 'time', 'convert'],

  supportedInputTypes: [SEMANTIC_TYPES.TIMESTAMP, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'direction',
      type: 'select',
      label: 'Conversion',
      defaultValue: 'auto',
      options: ['auto', 'toDate', 'toTimestamp'],
      description: 'auto: detect input format, toDate: timestamp → date, toTimestamp: date → timestamp',
    },
    {
      name: 'format',
      type: 'select',
      label: 'Output Format',
      defaultValue: 'iso',
      options: ['iso', 'locale', 'utc'],
      description: 'Date output format',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const direction = (config.direction as string) || 'auto';
    const format = (config.format as string) || 'iso';
    const trimmed = input.trim();

    // Auto-detect direction
    let isTimestamp = direction === 'toDate';
    if (direction === 'auto') {
      // If it's a number, treat as timestamp
      isTimestamp = /^\d+$/.test(trimmed);
    }

    if (isTimestamp || direction === 'toDate') {
      // Convert timestamp to date
      let timestamp = parseInt(trimmed, 10);
      
      // Handle both seconds and milliseconds
      if (timestamp < 10000000000) {
        timestamp *= 1000; // Convert seconds to milliseconds
      }

      const date = new Date(timestamp);
      
      switch (format) {
        case 'iso':
          return date.toISOString();
        case 'locale':
          return date.toLocaleString();
        case 'utc':
          return date.toUTCString();
        default:
          return date.toISOString();
      }
    } else {
      // Convert date to timestamp
      const date = new Date(trimmed);
      if (isNaN(date.getTime())) {
        return `Invalid date: "${trimmed}"`;
      }
      return Math.floor(date.getTime() / 1000).toString();
    }
  },
};

